import { CurrencyCodeSchema } from 'types/currency';
import { z } from 'zod/v4';

const LocalizedMoneyObjectSchema = z.object({
  currency: CurrencyCodeSchema.optional(),
  total_htva: z.string().nullable(),
  total_tvac: z.string(),
  remaining_balance: z.string(),
  remaining_balance_with_fees: z.string(),
  total_discount_amount: z.string().nullable(),
  late_fees: z.string(),
  remaining_late_fees: z.string(),
  interest_amount: z.string(),
  fix_fee_amount: z.string(),
  penalty_clause_amount: z.string(),
  amount_already_paid: z.string(),
  terms_and_conditions: z.object({
    fix_fee: z.object({
      value: z.string(),
    }),
    penalty_clause: z.object({
      value: z.string(),
      minimum: z.string(),
    }),
  }),
});

export const LegacyAttachedInvoiceSchema = z
  .object({
    id: z.string(),
    attributes: z.object({
      reference: z.string(),
      issue_date: z.string(),
      due_date: z.string(),
      total_tvac: z.coerce.number(),
      late_fees: z.coerce.number(),
      remaining_balance_with_fees: z.string(),
      amount_already_paid: z.string(),
      currency: CurrencyCodeSchema.optional(),
      deleted: z.coerce.boolean(),
      localized_money_object: LocalizedMoneyObjectSchema,
      remaining_balance: z.string(),
      has_pdf: z.boolean(),
      additional_files: z.array(z.unknown()),
    }),
  })
  .transform(({ id, attributes }) => ({
    id,
    ...attributes,
  }));

export const LegacyAttachedInvoicesSchema = z.object({
  details: z.array(LegacyAttachedInvoiceSchema),
  total_tvac: z.coerce.number(),
  total_late_fees: z.coerce.number(),
  paid_amount: z.coerce.number(),
  total_due: z.coerce.number(),
});

export type AttachedInvoice = z.infer<typeof LegacyAttachedInvoiceSchema>;
export type AttachedInvoices = z.infer<typeof LegacyAttachedInvoicesSchema>;

export const AttachedInvoiceSchema = z
  .object({
    id: z.coerce.string(),
    reference: z.string(),
    issueDate: z.string(),
    dueDate: z.string(),
    totalTvac: z.coerce.number(),
    lateFees: z.coerce.number(),
    remainingBalanceWithFees: z.string(),
    amountAlreadyPaid: z.string(),
    currency: CurrencyCodeSchema.optional(),
    deleted: z.coerce.boolean(),
    localizedMoneyObject: LocalizedMoneyObjectSchema,
    remainingBalance: z.string(),
    hasPDF: z.boolean(),
    additionalFiles: z.array(z.unknown()),
  })
  .transform(
    (invoice): AttachedInvoice => ({
      id: invoice.id,
      reference: invoice.reference,
      issue_date: invoice.issueDate,
      due_date: invoice.dueDate,
      total_tvac: invoice.totalTvac,
      late_fees: invoice.lateFees,
      remaining_balance_with_fees: invoice.remainingBalanceWithFees,
      amount_already_paid: invoice.amountAlreadyPaid,
      currency: invoice.currency,
      deleted: invoice.deleted,
      localized_money_object: invoice.localizedMoneyObject,
      remaining_balance: invoice.remainingBalance,
      has_pdf: invoice.hasPDF,
      additional_files: invoice.additionalFiles,
    }),
  );

export const AttachedInvoicesSchema = z
  .object({
    details: z.array(AttachedInvoiceSchema),
    totalTvac: z.coerce.number(),
    totalLateFees: z.coerce.number(),
    paidAmount: z.coerce.number(),
    totalDue: z.coerce.number(),
  })
  .transform(
    (invoices): AttachedInvoices => ({
      details: invoices.details,
      total_tvac: invoices.totalTvac,
      total_late_fees: invoices.totalLateFees,
      paid_amount: invoices.paidAmount,
      total_due: invoices.totalDue,
    }),
  );
